'use client';

import React, { useEffect, useState } from 'react';
import { JobDetail } from '~/types/job-detail';
import { addFavorite, removeFavorite } from '~/app/actions/favorites-action';
import { getCookieAction } from '~/app/actions/client/get-cookie-action';
import { setCookieAction } from '~/app/actions/client/set-cookie-action';
import { useFavoriteStore } from '~/stores/favorite-store';

interface FeaturedJobFavoriteButtonProps {
  job: JobDetail;
  favoriteJobIds: number[];
  isSignedIn?: boolean;
}

const FeaturedJobFavoriteButton: React.FC<FeaturedJobFavoriteButtonProps> = ({
  job,
  favoriteJobIds,
  isSignedIn = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    isFavorite,
    addFavorite: addToStore,
    removeFavorite: removeFromStore,
    initializeFavorites,
  } = useFavoriteStore();

  // 初期お気に入り状態
  useEffect(() => {
    if (isSignedIn) {
      initializeFavorites(favoriteJobIds);
    } else {
      (async () => {
        const favoriteJobIdsFromCookie = JSON.parse(
          (await getCookieAction('favorite_job_ids')) || '[]',
        );
        initializeFavorites(favoriteJobIdsFromCookie);
      })();
    }
  }, [favoriteJobIds, isSignedIn, initializeFavorites]);

  // ★ この toggleFavorite のロジックだけ JobDetailsFloatingButtons と同様にしました
  const toggleFavorite = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      if (isSignedIn) {
        if (isFavorite(job.id)) {
          await removeFavorite(job.id);
          removeFromStore(job.id);
        } else {
          await addFavorite(job.id);
          addToStore(job.id);
        }
      } else {
        const favorite_job_ids = JSON.parse(
          (await getCookieAction('favorite_job_ids')) || '[]',
        );

        if (isFavorite(job.id)) {
          const updatedIds = favorite_job_ids.filter(
            (id: number) => id !== job.id,
          );
          await setCookieAction({
            key: 'favorite_job_ids',
            value: JSON.stringify(updatedIds),
          });
          removeFromStore(job.id);
        } else {
          const updatedIds = [...favorite_job_ids, job.id];
          await setCookieAction({
            key: 'favorite_job_ids',
            value: JSON.stringify(updatedIds),
          });
          addToStore(job.id);
        }
      }
    } catch (err) {
      console.error('お気に入り処理失敗:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <button
      type="button"
      className="p-featured-jobs__favorite-button js-favorite-button"
      data-job-id={job.id}
      data-is-favorite={String(isSignedIn && isFavorite(job.id))}
      data-guest-favorite={String(!isSignedIn && isFavorite(job.id))}
      onClick={toggleFavorite}
      disabled={isProcessing}
    />
  );
};

export default FeaturedJobFavoriteButton;
